// Turns the UI parameters into a per-sample model of each generator, for the real-time engine.
// Every generator boils down to:
//   points along the groove at `pointRate` per second (output time),
//   each point taking the input value `inputStep` samples after the previous one,
//   optionally low-passed first, quantized to the cutter/printer grid, and (original laser) dropped
//   when closer than minDist to the previous kept point.

import type { Mode, Values } from "../params";

export interface AlgoConfig {
  /** Groove points per second of playback. */
  pointRate: number;
  /** Input samples consumed per point. inputStep·pointRate/sr ≠ 1 means a speed/pitch error. */
  inputStep: number;
  /** Anti-alias low-pass before sampling the points (Hz), 0 = none. */
  cutoff: number;
  /** Peak displacement in grid steps (px or print layers); 0 = no quantization. */
  ampSteps: number;
  /** Peak displacement in px, for the minDist test. */
  ampPx: number;
  /** Arc length between consecutive points (px). */
  arcPx: number;
  /** Points closer than this (px) to the last kept one are dropped; 0 = never. */
  minDistPx: number;
  channel: "left" | "mono" | "right" | "side";
  prep: { hpf: number; driveDb: number; limiter: boolean; preemph: boolean } | null;
  magnetic: boolean;
  stereo3d: boolean;
  /** When playback lags input by more than this (samples), skip ahead (≈ one revolution). */
  maxLag: number;
}

export interface LiveInfo {
  radius: number | null;
  original: string;
  improved: string;
}

const TAU = 2 * Math.PI;

export function liveConfigs(mode: Mode, v: Values, radius: number, sr: number): { original: AlgoConfig; improved: AlgoConfig; info: LiveInfo } {
  const magnetic = v.cartridge === "magnetic";
  const quant = !!v.simQuantize;
  const prep = { hpf: Number(v.hpf), driveDb: Number(v.driveDb), limiter: !!v.limiter, preemph: magnetic };
  const channel = (v.channel as AlgoConfig["channel"]) ?? "mono";
  const dpi = v.dpi as number;
  let original: AlgoConfig, improved: AlgoConfig;
  let info: LiveInfo;

  if (mode === "spiral") {
    const rpm = v.rpm as number;
    const thetaIter = v.thetaIter as number;
    const revSamples = (sr * 60) / rpm;
    const indexIncr = Math.max(1, Math.trunc(revSamples / thetaIter));
    const amp = v.amplitude as number;
    const minDistIn = (v.minDist as number) / dpi;
    const pointRateO = (thetaIter * rpm) / 60;
    original = {
      pointRate: pointRateO, inputStep: indexIncr, cutoff: 0,
      ampSteps: quant ? amp : 0, ampPx: amp,
      arcPx: (TAU * radius * dpi) / thetaIter, minDistPx: v.minDist as number,
      channel: "left", prep: null, magnetic, stereo3d: false, maxLag: revSamples,
    };
    const pointRateI = ((rpm / 60) * TAU * radius) / minDistIn;
    improved = {
      pointRate: pointRateI, inputStep: sr / pointRateI, cutoff: Math.min(0.45 * pointRateI, 0.45 * sr),
      ampSteps: quant ? amp : 0, ampPx: amp, arcPx: v.minDist as number, minDistPx: 0,
      channel, prep, magnetic, stereo3d: false, maxLag: revSamples,
    };
    const keep = Math.min(1, ((v.minDist as number) / original.arcPx) ** -1);
    info = {
      radius,
      original: `${fmtHz(pointRateO)} points, every ${indexIncr}th sample, no filter` +
        (original.arcPx < original.minDistPx ? ` · drops ~${Math.round((1 - keep) * 100)}% of points here` : "") +
        (Math.abs((indexIncr * pointRateO) / sr - 1) > 1e-3 ? ` · plays ${(((indexIncr * pointRateO) / sr - 1) * 100).toFixed(1)}% off speed` : ""),
      improved: `${fmtHz(pointRateI)} points, low-pass ${fmtHz(improved.cutoff)}`,
    };
  } else if (mode === "linear") {
    const amp = v.amplitude as number;
    // original: one point per sample; amplitude applied twice (amp²/dpi·72 pt → amp² px at 1200 dpi… in px: amp·amp·72/dpi·dpi/72)
    const ampO = amp * amp;
    const speedO = ((v.xScale as number) / dpi) * sr;
    original = {
      pointRate: sr, inputStep: 1, cutoff: 0, ampSteps: quant ? ampO : 0, ampPx: ampO,
      arcPx: v.xScale as number, minDistPx: 0, channel: "left", prep: null, magnetic, stereo3d: false, maxLag: sr,
    };
    const step = (v.minDist as number) / dpi;
    const pointRateI = (v.speed as number) / step;
    improved = {
      pointRate: pointRateI, inputStep: sr / pointRateI, cutoff: Math.min(0.45 * pointRateI, 0.45 * sr),
      ampSteps: quant ? amp : 0, ampPx: amp, arcPx: v.minDist as number, minDistPx: 0,
      channel, prep, magnetic, stereo3d: false, maxLag: sr,
    };
    info = {
      radius: null,
      original: `44.1 kHz points at ${speedO.toFixed(1)} in/s, wiggle ${ampO} px (scaled twice)`,
      improved: `${fmtHz(pointRateI)} points at ${v.speed} in/s, low-pass ${fmtHz(improved.cutoff)}`,
    };
  } else {
    const rpm = v.rpm as number;
    const layers = v.amplitude as number;
    const rd = v.rateDivisor as number;
    original = {
      pointRate: sr / rd, inputStep: rd, cutoff: 0, ampSteps: quant ? layers : 0, ampPx: layers,
      arcPx: 1, minDistPx: 0, channel: "left", prep: null, magnetic, stereo3d: true, maxLag: sr,
    };
    const N = (v.ptsPerRev as number) > 0 ? (v.ptsPerRev as number) : Math.round(TAU * (v.outerRad as number) * dpi);
    const pointRateI = (N * rpm) / 60;
    improved = {
      pointRate: pointRateI, inputStep: sr / pointRateI, cutoff: Math.min(0.45 * pointRateI, 0.45 * sr),
      ampSteps: quant ? layers : 0, ampPx: layers, arcPx: 1, minDistPx: 0,
      channel, prep, magnetic, stereo3d: true, maxLag: sr,
    };
    info = {
      radius: null,
      original: `every ${rd}th sample (${fmtHz(sr / rd)}), no filter, ±${layers} layers`,
      improved: `${fmtHz(pointRateI)} points, low-pass ${fmtHz(improved.cutoff)}, ±${layers} layers`,
    };
  }
  return { original, improved, info };
}

function fmtHz(x: number) {
  return x >= 1000 ? `${(x / 1000).toFixed(2)} kHz` : `${x.toFixed(0)} Hz`;
}
